/**
 * BMP-HOOK:gallery-color-select
 *
 * Two-way sync between the product gallery and the Color option, both keyed
 * off the media's Alt Text (see snippets/slideshow-controls.liquid, which
 * stamps `data-media-color` on any thumbnail whose alt exactly (case-
 * insensitive) matches a real Color option value):
 *
 *  1. Click a gallery thumbnail -> select the matching Color option.
 *  2. Select a Color option -> once the section's own re-render finishes,
 *     jump the gallery to that color's thumbnail.
 *  3. On first page load, if nothing was deep-linked via ?variant=, make the
 *     default selected color match whichever image is FIRST in the gallery
 *     (the hero shot), instead of whatever the Color option list happens to
 *     list first.
 *
 * 2026-10-31 fix (2-sided shirts): each color can have MORE THAN ONE image
 * with the same alt text (back mockup + front mockup). Previously (2) always
 * jumped to the FIRST image of the color, so clicking the front thumbnail
 * flashed the front, then the re-render landed and (2) jumped back to the
 * back image. Now (1) remembers which of that color's images was clicked
 * (its position among same-color thumbnails), and (2) returns to that same
 * position in the fresh gallery. Plain color-swatch selections (no thumbnail
 * click) still go to the first image of the color, as before.
 *
 * Why (2) waits for the section re-render: selecting a Color option makes
 * variant-picker.js fetch a freshly rendered copy of the section, and
 * media-gallery.js then replaces the WHOLE <media-gallery> element. Clicking
 * a thumbnail before that would land on the old, discarded gallery. So this
 * listens for StandardEvents.productSelect and waits on its `event.promise`,
 * exactly as media-gallery.js does, guaranteeing it runs on the NEW gallery.
 *
 * All three hand off into the theme's own native handlers via .click().
 */
import { StandardEvents } from '@shopify/events';

const norm = (s) => (s || '').trim().toLowerCase();

// Set by a thumbnail click (1), consumed by the next re-render (2).
let bmpPendingThumb = null; // { color, index }

function findColorInput(scope, color) {
  const picker = scope.querySelector('variant-picker');
  if (!picker) return null;

  return Array.from(picker.querySelectorAll('input[data-option-name="Color"]')).find(
    (el) => norm(el.value) === norm(color)
  );
}

function findThumbnails(scope, color) {
  return Array.from(scope.querySelectorAll('[data-media-color]')).filter(
    (el) => norm(el.dataset.mediaColor) === norm(color)
  );
}

// 1. Thumbnail click -> select the matching Color option.
document.addEventListener(
  'click',
  (event) => {
    // Ignore the programmatic clicks this file makes in (2)/(3).
    if (!event.isTrusted) return;

    const thumb = event.target.closest('[data-media-color]');
    if (!thumb) return;

    const color = thumb.dataset.mediaColor;
    if (!color) return;

    const scope = thumb.closest('.shopify-section, dialog') || document;
    const input = findColorInput(scope, color);

    if (input && !input.checked) {
      const index = findThumbnails(scope, color).indexOf(thumb);
      bmpPendingThumb = { color: norm(color), index: Math.max(index, 0) };
      input.click();
    }
  },
  { passive: true }
);

// 2. Color option selected -> once the section re-render lands, jump the
// gallery to the matching thumbnail (the one that was clicked, if any).
document.addEventListener(StandardEvents.productSelect, (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  const scope = event.target.closest('.shopify-section, dialog') || document;

  event.promise
    .then(() => {
      const input = scope.querySelector('variant-picker input[data-option-name="Color"]:checked');
      if (!input) return;

      const thumbs = findThumbnails(scope, input.value);
      let target = thumbs[0];

      if (bmpPendingThumb && bmpPendingThumb.color === norm(input.value) && thumbs[bmpPendingThumb.index]) {
        target = thumbs[bmpPendingThumb.index];
      }
      bmpPendingThumb = null;

      if (target) {
        target.click();
      }
    })
    .catch(() => {
      // Aborted/failed fetch (e.g. superseded by a faster follow-up
      // selection) -- nothing to sync against, safe to ignore.
    });
});

// 3. On load, with no ?variant= deep link present, default the selected
// color to whichever image is first in the gallery.
function bmpSyncDefaultColorToHeroImage() {
  if (new URLSearchParams(window.location.search).has('variant')) return;

  document.querySelectorAll('variant-picker').forEach((picker) => {
    const scope = picker.closest('.shopify-section, dialog') || document;
    const heroThumb = scope.querySelector('[data-media-color]');
    if (!heroThumb) return;

    const color = heroThumb.dataset.mediaColor;
    const input = findColorInput(scope, color);

    if (input && !input.checked) {
      input.click();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bmpSyncDefaultColorToHeroImage, { once: true });
} else {
  bmpSyncDefaultColorToHeroImage();
}
